import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Badge, SpacerVertical, Typography } from '@klarna/bubble-ui';
import { withPolling } from 'moet-core';
import { ContainerTemplate } from '@/components';
import { Audience, ExitPointType } from '@/bpc/types/BpcPayload';
import { isFetchingBpc } from '@/selectors/loadingStates';
import { getBpcStatus, getBusinessAwareArrayTranslator, getBusinessAwareTranslator, getBusinessName, getContractOnboardingType, getExitPoints, } from '@/selectors/bpc';
import { ContractOnboardingStatus } from '@/bpc/mf-fields/enm/ContractOnboardingStatus';
import { ColoredIllustration } from './ColoredIllustration';
import { SmallIcon } from './SmallIcon';
import { Paragraphs, TextWithNewlines } from './Paragraphs';
import { getPollingInterval, SLOW_POLLING_INTERVAL, startPolling, STATUS_OVERRIDES, stopPolling, REDIRECT_TIMEOUT_IN_MILLISECONDS, } from './constants';
import ExitPoints from './ExitPoints';
import { ContractOnboardingType } from '@/bpc/mf-fields/enm/ContractOnboardingType';
function makeNavigateToExitPointFn(exitPoints, setExitPointTranslationPath, logExitPoint) {
    return function navigateToExitPoint() {
        const relevantExitPoints = exitPoints === null || exitPoints === void 0 ? void 0 : exitPoints.filter(ep => ep.audience === Audience.WEB);
        if (!relevantExitPoints || !relevantExitPoints.length) {
            return;
        }
        const redirectExitPoint = relevantExitPoints.find(ep => ep.type === ExitPointType.REDIRECT);
        if (redirectExitPoint) {
            logExitPoint(redirectExitPoint.exit_point);
            window.location.href = redirectExitPoint.exit_point;
        }
        else {
            logExitPoint(relevantExitPoints[0].exit_point);
            setExitPointTranslationPath(relevantExitPoints[0].exit_point);
        }
    };
}
export const _OnboardingStatusComponent = ({ businessName, status, error, exitPoints = [], fetchOnboarding, t, tArray, logExitPoint, logStatusEvent, contractOnboardingType, }) => {
    const tPrefix = useMemo(() => `group.status.${STATUS_OVERRIDES[status] || status}`, [status]);
    const [exitPointTranslationPath, setExitPointTranslationPath] = useState();
    const [currentStatus, setCurrentStatus] = useState();
    if (status !== currentStatus || !currentStatus) {
        logStatusEvent(currentStatus, status);
        setCurrentStatus(status);
    }
    const navigateToExitPoint = makeNavigateToExitPointFn(exitPoints, setExitPointTranslationPath, logExitPoint);
    useEffect(() => {
        fetchOnboarding();
    }, []);
    useEffect(() => {
        if ((contractOnboardingType === ContractOnboardingType.UPSELL_WEBSTORE_TO_ADS_MANAGER &&
            status === ContractOnboardingStatus.ONBOARDING_COMPLETED) ||
            (contractOnboardingType === ContractOnboardingType.UPSELL_WEBSTORE_TO_CONTENT_MANAGER &&
                status === ContractOnboardingStatus.ONBOARDING_COMPLETED)) {
            const timer = setTimeout(navigateToExitPoint, REDIRECT_TIMEOUT_IN_MILLISECONDS);
            return () => clearTimeout(timer);
        }
    }, [status, contractOnboardingType]);
    const exitPointsComponentProps = {
        status,
        exitPoints,
        exitPointTranslationPath,
        navigateToExitPoint,
        t,
        tPrefix,
    };
    return (React.createElement(ContainerTemplate, { error: error },
        React.createElement(ColoredIllustration, { status: status }),
        React.createElement(SpacerVertical, { small: true }),
        React.createElement(Typography, { textToken: "text-style/headings/titles/bold/primary" },
            React.createElement(TextWithNewlines, null, t(`${tPrefix}.title`))),
        React.createElement(SpacerVertical, { small: true }),
        t(`${tPrefix}.badge`) ? React.createElement(Badge, null, t(`${tPrefix}.badge`)) : null,
        React.createElement(SpacerVertical, { small: true }),
        React.createElement(Paragraphs, { tKey: `${tPrefix}.paragraphs`, params: { businessName }, tArray: tArray }),
        React.createElement(SpacerVertical, { spaceToken: "space.200" }),
        React.createElement(ExitPoints, Object.assign({}, exitPointsComponentProps)),
        React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
            React.createElement("div", null,
                React.createElement(SmallIcon, { status: status })),
            React.createElement(Typography, { textToken: "text-style/text/super-paragraphs/body/bold" },
                React.createElement(TextWithNewlines, null,
                    " ",
                    t(`${tPrefix}.second_title`)))),
        React.createElement(SpacerVertical, { small: true }),
        React.createElement(Paragraphs, { tKey: `${tPrefix}.second_paragraphs`, params: { businessName }, tArray: tArray })));
};
/* istanbul ignore next */
export const OnboardingStatusComponent = compose(connect(state => ({
    isFetchingBpc: isFetchingBpc(state),
    status: getBpcStatus(state),
    businessName: getBusinessName(state),
    contractOnboardingType: getContractOnboardingType(state),
    exitPoints: getExitPoints(state),
    t: getBusinessAwareTranslator(state),
    tArray: getBusinessAwareArrayTranslator(state),
})), withPolling(({ fetchOnboarding }) => fetchOnboarding(), ({ isFetchingBpc: wasFetchingBpc }, { isFetchingBpc }) => wasFetchingBpc && !isFetchingBpc, {
    intervalCriteria: (_prevProps, { status }) => getPollingInterval(status),
    startCriteria: (_prevProps, { status }) => startPolling(status),
    exitCriteria: (_prevProps, { status }) => stopPolling(status),
    maxDelayTime: SLOW_POLLING_INTERVAL,
}))(_OnboardingStatusComponent);
